import { Fragment } from "react";
import { ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ActionType, ColumnType } from "../../types/data-table";
import { getObjectValueByString } from "../../utils/get-object-value-by-string";
import "./dataTable.css";

type Props = {
  columns: ColumnType[];
  data: any[] | undefined;
  actions?: ActionType[];
  headerBackground?: string;
  showSerial?: boolean;
  variant?: "large" | "small";
  loading?: boolean;
};

export default function DataTable({
  columns,
  data,
  actions,
  showSerial = false,
  headerBackground = "",
  variant = "large",
  loading = false,
}: Props) {
  const { t } = useTranslation(["common"]);
  const totalColumns =
    columns.length + (showSerial ? 1 : 0) + (actions ? 1 : 0);
  return (
    <table className="table rounded-2">
      <thead
        className={`${
          variant === "large" ? "table-header" : "claims-table-header"
        } ${headerBackground}`}
      >
        <tr>
          {showSerial && (
            <th className="p-4">{t("table_head.serial", { ns: "common" })}</th>
          )}
          {columns.map((column) => {
            return (
              <th
                className={`${
                  variant === "large" ? "px-2 py-4" : "px-2 py-2"
                } text-${column.headerAlignment || "left"}`}
                key={column.accessor}
              >
                {column.header}
              </th>
            );
          })}
          {actions && actions.length > 0 ? (
            <th
              className={`${
                variant === "large" ? "px-2 py-4" : "px-2 py-2"
              } text-center`}
            >
              {t("table_head.action", { ns: "common", count: actions.length })}
            </th>
          ) : undefined}
        </tr>
      </thead>
      <tbody className="bg-white">
        {loading && (
          <tr>
            <td className="p-0" colSpan={totalColumns}>
              <ProgressBar
                animated={true}
                variant="info"
                now={100}
                className="rounded-0"
                style={{ height: "10px" }}
              />
            </td>
          </tr>
        )}
        {data && Array.isArray(data) && data.length > 0 ? (
          data.map((item, index) => {
            return (
              <tr key={item?._id}>
                {showSerial && (
                  <td
                    className={`${
                      variant === "large" ? "p-4" : "px-3 py-2"
                    } align-middle`}
                  >
                    {index + 1}
                  </td>
                )}
                {columns.map((column) => {
                  return (
                    <td
                      className={`text-${
                        column.alignment || "left"
                      } align-middle`}
                      key={`${column.accessor}+${item?._id}`}
                    >
                      {column.formatDisplay
                        ? column.formatDisplay(item)
                        : getObjectValueByString(item, column.accessor) ?? "-"}
                    </td>
                  );
                })}
                {actions ? (
                  <td
                    className={`${
                      variant === "large" ? "p-4" : "px-3 py-2"
                    } align-middle d-flex justify-content-center align-items-center gap-4`}
                  >
                    {actions.map((action) => {
                      return (
                        <Fragment key={action.name}>
                          {action.action(item)}
                        </Fragment>
                      );
                    })}
                  </td>
                ) : undefined}
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={totalColumns} className="text-center">
              {t("no_data", { ns: "common" })}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
